import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Box,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import { setMessage, useMaterialUIController } from "context";
import { IEventoResponse } from "interfaces/eventos";
import { deleteEvento } from "services/evento";

import {
  DELETE_EVENTO_ACCENT,
  deleteEventoDangerSectionSx,
  deleteEventoSectionSx,
  deleteEventoToolbarSx,
} from "./delete-evento.theme";

interface Props {
  evento: IEventoResponse;
}

function StatCard({
  label,
  value,
  accent,
  danger,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
  danger?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: danger
          ? "rgba(211, 47, 47, 0.3)"
          : accent
            ? "rgba(249, 96, 48, 0.3)"
            : "grey.200",
        bgcolor: danger
          ? "rgba(211, 47, 47, 0.04)"
          : accent
            ? "rgba(249, 96, 48, 0.04)"
            : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={
          danger ? DELETE_EVENTO_ACCENT : accent ? "#F96030" : "text.primary"
        }
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function DeleteEvento({ evento }: Props) {
  const navigate = useNavigate();
  const [, dispatch] = useMaterialUIController();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { inscricaoEvento, inscritos } = evento._count;
  const podeExcluir = inscritos === 0 && inscricaoEvento === 0;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { status } = await deleteEvento(evento.id);
      if (status === 200) {
        setMessage(dispatch, {
          open: true,
          text: "Evento excluído com sucesso",
          severity: "success",
        });
        navigate("/layouts/pages/cadastros/eventos");
      }
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        text:
          error instanceof Error ? error.message : "Falha ao excluir o evento",
        severity: "error",
      });
    } finally {
      setDeleting(false);
      setOpenConfirm(false);
    }
  };

  return (
    <MDBox>
      <MDDialog
        title="Excluir evento"
        message={`Tem certeza que deseja excluir permanentemente o evento "${evento.titulo}"? Esta ação não pode ser desfeita.`}
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        response={(resp: boolean) => {
          if (resp) void handleDelete();
          else setOpenConfirm(false);
        }}
      />

      <Box sx={deleteEventoToolbarSx}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <DeleteOutlineOutlinedIcon sx={{ color: DELETE_EVENTO_ACCENT }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Excluir evento
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {evento.titulo}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={6} sm={4}>
          <StatCard
            label="Nº de inscrições"
            value={inscricaoEvento}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard label="Atletas inscritos" value={inscritos} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Status de exclusão"
            value={podeExcluir ? "Permitida" : "Bloqueada"}
            danger={!podeExcluir}
          />
        </Grid>
      </Grid>

      <Box sx={{ ...deleteEventoSectionSx, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
          <ReceiptLongOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Verificação de vínculos
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          O evento só pode ser excluído quando não houver inscrições nem atletas
          vinculados. Revise os números acima antes de prosseguir.
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ReceiptLongOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2">
              Inscrições: <strong>{inscricaoEvento}</strong>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupsOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2">
              Atletas: <strong>{inscritos}</strong>
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Box sx={deleteEventoDangerSectionSx}>
        <Stack direction="row" spacing={1} alignItems="flex-start" mb={1.5}>
          <WarningAmberOutlinedIcon sx={{ color: DELETE_EVENTO_ACCENT, mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Zona de exclusão
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {podeExcluir
                ? "Esta ação remove o evento e todos os dados associados de forma permanente."
                : "Não é possível excluir enquanto existirem inscrições ou atletas neste evento."}
            </Typography>
          </Box>
        </Stack>

        {podeExcluir ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tem certeza que deseja excluir este evento? Você será solicitado a
            confirmar antes da exclusão definitiva.
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mb: 2 }}>
            Este evento não pode ser excluído, pois há atletas ou inscrições
            vinculadas.
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="caption" color="text.secondary">
            {podeExcluir
              ? "Operação irreversível após confirmação."
              : "Operação não permitida no momento."}
          </Typography>
          <MDButton
            variant="outlined"
            color="error"
            disabled={!podeExcluir || deleting}
            onClick={() => setOpenConfirm(true)}
            startIcon={<DeleteOutlineOutlinedIcon />}
            sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "auto" } }}
          >
            {deleting ? "Excluindo…" : "Excluir evento"}
          </MDButton>
        </Stack>
      </Box>
    </MDBox>
  );
}
